import React from 'react';
import {
  DarkModeIcon,
  Divider,
  InfoIcon,
  LightModeIcon,
  Switch,
  Text,
} from '@billda/design-system';
import { useThemeStore } from '../../../shared/stores/themeStore';
import { useDensityStore } from '../../../shared/stores/densityStore';
import { VersionInfo } from '../../../shared/components/ui/VersionInfo';

/**
 * General settings area: about/version and appearance (theme, density).
 */
export const GeneralArea: React.FC = () => {
  const { mode, toggleMode } = useThemeStore();
  const { density, setDensity } = useDensityStore();

  return (
    <>
      {/* About Section */}
      <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
        <div className="mb-4 flex items-center">
          <InfoIcon
            aria-hidden="true"
            className="mr-2 size-5 text-content-accent"
          />
          <Text variant="h6">About</Text>
        </div>

        <Text as="div" variant="body2" color="secondary" className="mb-6">
          Application information and version details.
        </Text>

        <Divider className="mb-6" />

        <VersionInfo />
      </div>

      {/* Appearance Settings */}
      <div className="mb-6 rounded-xl border border-border-default bg-surface-default p-6">
        <div className="mb-4 flex items-center">
          {mode === 'light' ? (
            <LightModeIcon
              aria-hidden="true"
              className="mr-2 size-5 text-content-accent"
            />
          ) : (
            <DarkModeIcon
              aria-hidden="true"
              className="mr-2 size-5 text-content-accent"
            />
          )}
          <Text variant="h6">Appearance</Text>
        </div>

        <Text as="div" variant="body2" color="secondary" className="mb-6">
          Customize how Billda looks on your device.
        </Text>

        <Divider className="mb-6" />

        <div className="flex flex-col gap-6">
          <div>
            <label
              htmlFor="theme-mode-switch"
              className="flex cursor-pointer items-center gap-4"
            >
              <Switch
                id="theme-mode-switch"
                checked={mode === 'dark'}
                onCheckedChange={toggleMode}
                data-testid="theme-mode-switch"
              />
              <span>
                <Text as="span" variant="body1" className="block font-medium">
                  Dark Mode
                </Text>
                <Text as="span" variant="body2" color="secondary">
                  {mode === 'dark'
                    ? 'Dark mode is enabled'
                    : 'Switch to dark mode for reduced eye strain'}
                </Text>
              </span>
            </label>
          </div>

          <div>
            <label
              htmlFor="density-toggle"
              className="flex cursor-pointer items-center gap-4"
            >
              <Switch
                id="density-toggle"
                checked={density === 'comfortable'}
                onCheckedChange={checked =>
                  setDensity(checked ? 'comfortable' : 'compact')
                }
                data-testid="density-toggle"
              />
              <span>
                <Text as="span" variant="body1" className="block font-medium">
                  Comfortable Display
                </Text>
                <Text as="span" variant="body2" color="secondary">
                  {density === 'comfortable'
                    ? 'Comfortable mode - larger text and spacing'
                    : 'Compact mode - smaller text, more content visible'}
                </Text>
              </span>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};
